import React, { useState } from 'react';
import { motion } from 'motion/react';
import { AdvertVideo } from './AdvertVideo';
import { 
  MapPin, 
  Zap, 
  Droplets, 
  TrendingUp, 
  Users, 
  Shield, 
  Clock,
  CheckCircle,
  ArrowRight,
  Play,
  Download,
  Share2,
  Star
} from 'lucide-react';

interface AdvertPageProps {
  onGetStarted?: () => void;
  onClose?: () => void;
}

export function AdvertPage({ onGetStarted, onClose }: AdvertPageProps) {
  const [showVideo, setShowVideo] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-emerald-900 to-teal-900 text-white overflow-x-hidden">
      {/* Hero Section */}
      <div className="relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-between items-center mb-12"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-lg">
                <Users className="size-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-black">SENTLO</h1>
                <p className="text-xs text-emerald-300">Intelligence Network</p>
              </div>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="text-white/60 hover:text-white transition-colors"
              >
                ✕
              </button>
            )}
          </motion.div>

          {/* Main Headline */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full px-4 py-2 mb-6">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-sm font-bold text-emerald-300">NIGERIA'S #1 COMMUNITY NETWORK</span>
              </div>

              <h2 className="text-4xl md:text-6xl font-black mb-6 leading-tight">
                Your City's
                <br />
                <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                  Real-Time Intelligence
                </span>
                <br />
                Network 🇳🇬
              </h2>

              <p className="text-xl md:text-2xl text-white/90 leading-relaxed mb-8 font-medium">
                Sentlo is a <strong className="text-emerald-300">hyperlocal intelligence network</strong> that turns every citizen into a neighborhood reporter, creating a <strong className="text-teal-300">real-time, verified, actionable information layer</strong> over Nigeria's cities.
              </p>

              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 mb-8">
                <p className="text-lg leading-relaxed">
                  Helping millions navigate <strong className="text-red-300">traffic</strong>, <strong className="text-yellow-300">power outages</strong>, <strong className="text-purple-300">fuel scarcity</strong>, <strong className="text-cyan-300">flooding</strong>, and daily urban challenges with confidence.
                </p>
              </div>

              <div className="flex flex-wrap gap-4 mb-8">
                <button
                  onClick={onGetStarted}
                  className="group bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-black px-8 py-4 rounded-full shadow-2xl hover:shadow-emerald-500/50 transition-all hover:scale-105 active:scale-95 flex items-center gap-2"
                >
                  Get Started Free
                  <ArrowRight className="size-5 group-hover:translate-x-1 transition-transform" />
                </button>

                <button
                  onClick={() => setShowVideo(true)}
                  className="group bg-white/10 backdrop-blur-md hover:bg-white/20 border border-white/30 text-white font-bold px-8 py-4 rounded-full transition-all hover:scale-105 active:scale-95 flex items-center gap-2"
                >
                  <Play className="size-5" />
                  Watch Video
                </button>
              </div>

              <div className="flex items-center gap-8">
                <div>
                  <div className="text-3xl font-black text-emerald-300">10K+</div>
                  <div className="text-sm text-white/60">Active Users</div>
                </div>
                <div>
                  <div className="text-3xl font-black text-teal-300">36</div>
                  <div className="text-sm text-white/60">States + FCT</div>
                </div>
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="size-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <div className="text-sm text-white/60">Community Rated</div>
                </div>
              </div>
            </motion.div>

            {/* Video Preview */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <AdvertVideo autoPlay={false} />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Tagline Section */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-2xl md:text-3xl font-black text-center"
          >
            It's not just an app—it's a <span className="underline decoration-4 decoration-yellow-300">community-powered survival tool</span> for modern Nigerian urban life. 🇳🇬🚦
          </motion.p>
        </div>
      </div>

      {/* Features Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h3 className="text-4xl md:text-5xl font-black mb-4">
            Eight Core Features
          </h3>
          <p className="text-xl text-white/80">
            Everything you need to navigate Nigerian urban life
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              icon: <Zap className="size-8" />,
              title: "Light Watch",
              description: "Track NEPA/PHCN power status across neighborhoods in real-time",
              color: "from-yellow-500 to-orange-500"
            },
            {
              icon: <MapPin className="size-8" />,
              title: "Traffic & Go-Slow",
              description: "Avoid gridlock with live updates from Third Mainland to Lekki",
              color: "from-red-500 to-pink-500"
            },
            {
              icon: <div className="text-2xl">⛽</div>,
              title: "Fuel Watch",
              description: "Find available fuel, compare prices, skip the queues",
              color: "from-purple-500 to-pink-500"
            },
            {
              icon: <Droplets className="size-8" />,
              title: "Flood Alerts",
              description: "Know which roads are passable during rainy season",
              color: "from-cyan-500 to-blue-500"
            },
            {
              icon: <TrendingUp className="size-8" />,
              title: "Market Prices",
              description: "Track rice, yam, tomato prices across Lagos markets",
              color: "from-emerald-500 to-teal-500"
            },
            {
              icon: <Users className="size-8" />,
              title: "Bus Stop Crowd",
              description: "Monitor danfo/BRT availability and wait times",
              color: "from-indigo-500 to-purple-500"
            },
            {
              icon: <div className="text-2xl">🤝</div>,
              title: "Digital Ajo Circles",
              description: "Community savings groups (Esusu) digitized & secure",
              color: "from-green-500 to-emerald-500"
            },
            {
              icon: <Shield className="size-8" />,
              title: "SafePay Escrow",
              description: "Secure marketplace transactions with trusted neighbors",
              color: "from-blue-500 to-cyan-500"
            }
          ].map((feature, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="group bg-white/5 backdrop-blur-md hover:bg-white/10 border border-white/10 rounded-2xl p-6 transition-all hover:scale-105 hover:border-white/30"
            >
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                <div className="text-white">{feature.icon}</div>
              </div>
              <h4 className="text-xl font-bold mb-2">{feature.title}</h4>
              <p className="text-white/70 text-sm leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-black/30 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h3 className="text-4xl md:text-5xl font-black mb-4">
              How Sentlo Works
            </h3>
            <p className="text-xl text-white/80">
              Simple. Powerful. Community-driven.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "Report What You See",
                description: "Stuck in traffic? See NEPA light up? Spot fuel at good price? Share it with your neighbors in seconds.",
                icon: <MapPin className="size-6" />
              },
              {
                step: "2",
                title: "Community Verifies",
                description: "Other users upvote, add details, or confirm. Trust Score ensures quality. Fake reports get flagged fast.",
                icon: <CheckCircle className="size-6" />
              },
              {
                step: "3",
                title: "Everyone Benefits",
                description: "Avoid go-slow, find fuel quickly, plan around power outages. Make smarter decisions with real-time data.",
                icon: <Users className="size-6" />
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2 }}
                className="relative"
              >
                {/* Connector Line */}
                {idx < 2 && (
                  <div className="hidden md:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-emerald-500 to-transparent z-0" />
                )}

                <div className="relative bg-white/5 backdrop-blur-md border border-white/20 rounded-2xl p-8 hover:bg-white/10 transition-all">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-2xl font-black shadow-lg">
                      {item.step}
                    </div>
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      {item.icon}
                    </div>
                  </div>
                  <h4 className="text-2xl font-bold mb-3">{item.title}</h4>
                  <p className="text-white/70 leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Real Scenarios */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h3 className="text-4xl md:text-5xl font-black mb-4">
            Real Nigerian Life, Real Solutions
          </h3>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {[
            {
              problem: "Morning commute from Lekki to VI",
              before: "❌ Stuck 2 hours in Third Mainland gridlock",
              after: "✅ Checked Sentlo, took Ikoyi route, arrived 30 mins",
              saved: "Saved 1.5 hours + fuel",
              color: "from-red-500 to-orange-500"
            },
            {
              problem: "Friday evening plans",
              before: "❌ NEPA takes light, generator no fuel, plans ruined",
              after: "✅ Sentlo showed light schedule, charged devices early",
              saved: "Party went on!",
              color: "from-yellow-500 to-amber-500"
            },
            {
              problem: "Weekend fuel hunting",
              before: "❌ Visited 4 stations, all empty, wasted 3 hours",
              after: "✅ Sentlo showed NNPC Ikoyi had fuel at ₦650, no queue",
              saved: "3 hours + stress",
              color: "from-purple-500 to-pink-500"
            },
            {
              problem: "Rainy season flooding",
              before: "❌ Drove into flooded Admiralty Way, car damaged",
              after: "✅ Sentlo alert 'waist-deep water', took Ajah route",
              saved: "₦500,000 repairs",
              color: "from-cyan-500 to-blue-500"
            }
          ].map((scenario, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="bg-white/5 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/10 transition-all"
            >
              <div className={`inline-flex px-4 py-2 rounded-full bg-gradient-to-r ${scenario.color} text-white text-sm font-bold mb-4`}>
                {scenario.problem}
              </div>
              <div className="space-y-3 mb-4">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">😰</span>
                  <div>
                    <div className="text-sm text-white/50 font-semibold">BEFORE SENTLO</div>
                    <div className="text-white/90">{scenario.before}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl">😎</span>
                  <div>
                    <div className="text-sm text-emerald-400 font-semibold">WITH SENTLO</div>
                    <div className="text-white">{scenario.after}</div>
                  </div>
                </div>
              </div>
              <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-xl p-3">
                <div className="text-emerald-300 font-bold text-center">💰 {scenario.saved}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Social Proof */}
      <div className="bg-black/30 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h3 className="text-4xl md:text-5xl font-black mb-4">
              Trusted by Thousands of Nigerians
            </h3>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote: "Sentlo saved me from 3-hour go-slow yesterday. I checked before leaving Victoria Island and took Falomo route. Best decision ever!",
                author: "Chioma A.",
                location: "Lagos",
                avatar: "👩🏾‍💼"
              },
              {
                quote: "As a generator technician, Light Watch helps me plan my day. I know exactly which areas need service before I even leave home.",
                author: "Ibrahim K.",
                location: "Abuja",
                avatar: "👨🏿‍🔧"
              },
              {
                quote: "Found fuel at ₦650 through Sentlo while my neighbors paid ₦720 elsewhere. Small thing but it adds up every week!",
                author: "Grace O.",
                location: "Port Harcourt",
                avatar: "👩🏿"
              }
            ].map((testimonial, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 }}
                className="bg-white/5 backdrop-blur-md border border-white/20 rounded-2xl p-6"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="size-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-white/90 mb-6 leading-relaxed italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="text-4xl">{testimonial.avatar}</div>
                  <div>
                    <div className="font-bold">{testimonial.author}</div>
                    <div className="text-sm text-white/60">{testimonial.location}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h3 className="text-4xl md:text-6xl font-black mb-6">
              Join 10,000+ Nigerians
            </h3>
            <p className="text-xl md:text-2xl mb-8 text-white/90">
              Making our neighborhoods safer, smarter, and better connected
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <button
                onClick={onGetStarted}
                className="group bg-white text-emerald-600 hover:bg-gray-100 font-black px-10 py-5 rounded-full shadow-2xl transition-all hover:scale-105 active:scale-95 flex items-center gap-2 text-lg"
              >
                Start Using Sentlo Free
                <ArrowRight className="size-6 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                className="group bg-white/10 backdrop-blur-md hover:bg-white/20 border-2 border-white text-white font-bold px-10 py-5 rounded-full transition-all hover:scale-105 active:scale-95 flex items-center gap-2 text-lg"
              >
                <Share2 className="size-5" />
                Share with Neighbors
              </button>
            </div>
            <p className="text-white/80">
              🇳🇬 Available across all 36 states + FCT | Free forever | No credit card required
            </p>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-black/50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                <Users className="size-5 text-white" />
              </div>
              <div>
                <div className="font-black text-lg">SENTLO</div>
                <div className="text-xs text-white/60">Community-Powered Intelligence</div>
              </div>
            </div>
            <div className="text-sm text-white/60 text-center md:text-left">
              © 2026 Sentlo. Built with ❤️ for Nigeria.
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen Video Modal */}
      {showVideo && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/95 backdrop-blur-lg z-50 flex items-center justify-center p-4"
          onClick={() => setShowVideo(false)}
        >
          <div className="w-full max-w-6xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end mb-4">
              <button
                onClick={() => setShowVideo(false)}
                className="text-white/60 hover:text-white text-2xl"
              >
                ✕
              </button>
            </div>
            <AdvertVideo 
              autoPlay={true} 
              onEnded={() => {
                // Keep video visible after it ends
              }}
            />
          </div>
        </motion.div>
      )}
    </div>
  );
}
